/* eslint eslint-comments/no-use: off */

import type {FilterProviderOptions, FilterSuggestion, SuppliedFilterProviderOptions} from '@github-ui/filter'
import {FilterProviderType} from '@github-ui/filter'
import {defaultFilterProviderOptions, NOT_SHOWN} from '@github-ui/filter/constants/defaults'
import {TIME_RANGE_VALUES} from '@github-ui/filter/constants/dynamic-filter-values'
import {DEFAULT_FILTER_PRIORITY} from '@github-ui/filter/constants/filter-constants'
import {NestedFilterProvider} from '@github-ui/filter/providers'
import {
  type IssueFieldSingleSelectOption,
  type IssueFieldType,
  KnownTypes,
} from '@github-ui/issue-fields-shared/issue-field'
import {NoteIcon} from '@primer/octicons-react'

export interface IssueFieldData {
  name: string
  name_slug: string
  options: IssueFieldSingleSelectOption[]
  data_type: IssueFieldType
}

interface IssueFieldsResponse {
  issue_fields?: IssueFieldData[]
}

const NUMERIC_FILTER_OPTIONS = {
  positive: true,
  negative: true,
  decimal: true,
}

const ISSUE_FIELD_FILTER_ENDPOINT = '/_filter/issue_fields'

// Cache to prevent duplicate requests
const providerCache = new Map<string, Promise<NestedFilterProvider>>()

export async function getIssueFieldFilterProvider(options: SuppliedFilterProviderOptions, repositoryScope?: string) {
  // Create a cache key based on the parameters
  const cacheKey = `${repositoryScope}-${JSON.stringify(options)}`

  // Return cached promise if it exists
  if (providerCache.has(cacheKey)) {
    return providerCache.get(cacheKey)!
  }

  // Create the promise and cache it immediately to prevent duplicate requests
  const providerPromise = createProvider(options, repositoryScope)
  providerCache.set(cacheKey, providerPromise)

  return providerPromise
}

async function createProvider(options: SuppliedFilterProviderOptions, repositoryScope?: string) {
  const endpoint = new URL(ISSUE_FIELD_FILTER_ENDPOINT, window.location.origin)

  if (repositoryScope) {
    endpoint.searchParams.set('repo', repositoryScope)
  }

  const jsonData = await fetchData(endpoint.toString())

  return new NestedFilterProvider(
    {
      key: 'field',
      displayName: 'Field',
      description: 'Filter by issue fields',
      priority: DEFAULT_FILTER_PRIORITY,
      icon: NoteIcon,
      subKeys: (jsonData?.issue_fields ?? [])
        .filter(node => !!node)
        .map(node => {
          // Map API response to IssueFieldData
          const fieldData: IssueFieldData = {
            name: node?.name ?? '',
            name_slug: node?.name_slug,
            options: node?.options,
            data_type: node?.data_type.toLowerCase() as IssueFieldType,
          }
          return buildSubKey(fieldData, options)
        }),
    },
    options,
  )
}

function buildSubKey(fieldData: IssueFieldData, options: SuppliedFilterProviderOptions) {
  const {name, data_type, name_slug} = fieldData

  // Build values based on data type
  let values: FilterSuggestion[] = []

  if (data_type === 'single_select') {
    values = (fieldData.options || []).map(option => ({
      value: option.name,
      displayName: option.name,
      priority: NOT_SHOWN,
      iconColor: option.color,
    }))
  } else if (data_type === 'date') {
    values = TIME_RANGE_VALUES
  }

  return {
    key: name_slug ?? name.toLowerCase().replace(/\s+/g, '-'),
    displayName: name,
    icon: KnownTypes[data_type].icon,
    type: getType(data_type),
    values,
    options: getFilterOptions(options, data_type),
  }
}

function getFilterOptions(options: SuppliedFilterProviderOptions, type: IssueFieldType): FilterProviderOptions {
  const baseOptions = {
    ...defaultFilterProviderOptions,
    filterTypes: {
      multiKey: true,
      multiValue: true,
      valueless: options.filterTypes?.valueless,
      hasValue: options.filterTypes?.hasValue,
    },
  }

  if (type === 'number') {
    return {
      ...baseOptions,
      ...NUMERIC_FILTER_OPTIONS,
    }
  }

  return baseOptions
}

async function fetchData(apiURL: string, abortController?: AbortController): Promise<IssueFieldsResponse | undefined> {
  let response
  try {
    response = await fetch(apiURL, {
      method: 'GET',
      mode: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      signal: abortController?.signal,
    })

    if (response?.ok) {
      return (await response.json()) as IssueFieldsResponse
    }
    return undefined
  } catch (error) {
    if (error instanceof Error && error.name !== 'AbortError') {
      throw error
    }
    return undefined
  }
}

const getType = (data_type?: IssueFieldType) => {
  switch (data_type) {
    case 'text':
      return FilterProviderType.Text
    case 'single_select':
      return FilterProviderType.Select
    case 'date':
      return FilterProviderType.Date
    case 'number':
      return FilterProviderType.Number
    default:
      return FilterProviderType.Text
  }
}
